import React from 'react';
import { useIntl } from 'react-intl';
import Divider from '@material-ui/core/Divider';
import SearchFilter from '@alltrails/shared/components/SearchFilter';
import useModalPortalElement from '@alltrails/shared/hooks/useModalPortalElement';
import User from '@alltrails/shared/types/User';
import ExpandableFilterPanel from 'components/explore/ExpandableFilterPanel';
import FilterInputFactory from 'components/explore/filters/FilterInputFactory';
import useFiltersContext from 'hooks/useFiltersContext';
import { FilterViewModel, FilterConfig, FilterInput } from 'types/Search';
import { getLabelsForKeys } from 'utils/filters/getAllAppliedFilterLabels';
import getSaveButtonChildrenProps from '../saveButtonChildrenProps';
import * as styles from './styles/styles.module.scss';

export type Props = {
  popperClassName?: string;
  sortedFilters: FilterViewModel[];
  title: React.ReactNode;
  triggerElement?: JSX.Element;
  user?: User;
};

/**
 * NestedPanel is like a special case of FilterPillFactory and could be collapsed into that.
 */
const NestedPanel = ({ popperClassName, sortedFilters = [], triggerElement, title, user }: Props) => {
  const intl = useIntl();

  const {
    appliedFilterLabelsByKey,
    clearAppliedFilters,
    clearDraftChanges,
    filtersByKey,
    computedResultsCount,
    isLoadingHypotheticalResultsCount,
    resetHypotheticalResultsCount,
    saveChanges,
    updateFilter
  } = useFiltersContext();

  const allFilterKeys = sortedFilters.map(filter => filter.key);

  let displayText;
  const appliedFilters = getLabelsForKeys(allFilterKeys, appliedFilterLabelsByKey);
  const isActive = appliedFilters.length > 0;
  switch (appliedFilters.length) {
    case 0:
      displayText = title;
      break;
    default:
      displayText = `${title} • ${appliedFilters.length}`;
      break;
  }

  const modalElement = useModalPortalElement();

  return (
    <SearchFilter
      clearButtonHandler={() => {
        clearAppliedFilters(allFilterKeys);
        resetHypotheticalResultsCount();
      }}
      forceModalState
      hasActiveFilters={isActive}
      modalElement={modalElement}
      modalTitle={title}
      pillButtonText={displayText}
      popperClassName={popperClassName}
      resetCurrentFormStateHandler={() => {
        clearDraftChanges();
        resetHypotheticalResultsCount();
      }}
      saveButtonChildren={getSaveButtonChildrenProps({ intl, isLoading: isLoadingHypotheticalResultsCount, resultsCount: computedResultsCount })}
      saveButtonHandler={saveChanges}
      triggerElement={triggerElement}
    >
      <>
        {sortedFilters.map((filter, index) => {
          // The UI treatment for star ratings is slightly different than other inputs.
          const panelTitle = filter.type === FilterInput.STAR_RATING ? null : filter.display;

          return (
            <React.Fragment key={filter.key}>
              <ExpandableFilterPanel
                className={styles.filterPanel}
                tabIndex={index}
                title={panelTitle}
                shouldTruncate={filter.truncate}
                isPro={filter.isProFilter}
              >
                <FilterInputFactory
                  filter={filtersByKey[filter.key]}
                  onChange={(changes: FilterConfig) => updateFilter(filter.key, changes)}
                  user={user}
                />
              </ExpandableFilterPanel>
              {index !== sortedFilters.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </>
    </SearchFilter>
  );
};

export default NestedPanel;
